import { Theme, css, useTheme } from '@emotion/react';

import { useEffect, useRef, useState } from 'react';

import { useUserQuery } from '@hooks/query/useUserQuery';
import { FontSizes, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowDown, ArrowUp, Icon } from 'chatie-design-system/icon';

import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EFinderImage from '@components/common/EFinderImage';
import RoundAvatar from '@components/common/RoundAvatar';
import Tag from '@components/common/Tag';
import Pad from '@components/layout/Pad';

import { useGlobalContext } from '@contexts/global';
import { IChatCharacter, IEFinderObject } from '@model';
import { linearGradient, media, safeArea } from '@styles/css';
import { formatComma, trimString } from '@utils/misc';

const BAR_COLOR = '#d9d9d9';

const coverImageCSS = (theme: Theme) => css`
  position: relative;
  padding-bottom: 62.5%;
  ${safeArea('top')}
  background-color:${theme.var.gray400};

  > div {
    position: absolute;
    width: 100%;
    height: 50%;
    padding: ${Spacing.medium}px;
    bottom: 0;
    color: ${theme.var.gray900};
    ${linearGradient('bottom', 'transparent', theme.true.white)}

    ${media.tablet} {
      padding: ${Spacing.xlarge}px;
    }
    ${media.desktop} {
      padding: ${Spacing.xxlarge}px;
    }
  }

  .likeBtn {
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: none;
    width: 40px;
    height: 40px;
    position: absolute;
    z-index: 5;
    bottom: 20px;
    right: 20px;
  }
`;

const profileCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.xhsmall}px;
  margin-bottom: ${Spacing.small}px;

  > div {
    margin-bottom: ${Spacing.xhsmall}px;
  }

  h3 {
    ${Typography.h5};
  }

  p {
    ${Typography.body12}
    color:${theme.var.gray500};
  }
  .character-avatar {
    position: relative;
  }
  .new-badge {
    display: flex;
    align-items: center;
    width: 100%;
    position: absolute;
    bottom: 0;
    white-space: nowrap;
  }
  .characterCreater {
    ${Typography.body12};
    color: ${theme.var.gray400};
    margin-top: ${Spacing.xxsmall}px;
  }
`;

const tagContainerCSS = (isOpen: boolean) => css`
  display: flex;
  justify-content: center;
  position: relative;
  padding: 0 47.5px;

  ${media.tablet} {
    padding: 0 ${Spacing.xlarge + 31.5}px;
  }
  ${media.desktop} {
    padding: 0 ${Spacing.xxlarge + 15.5}px;
  }

  .tagList {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    height: ${isOpen ? '100%' : '30px'};
    overflow: hidden;
    gap: ${Spacing.xsmall}px;

    .icon-text {
      display: flex;
      height: auto;
      padding: ${Spacing.xhsmall}px ${Spacing.xsmall}px;
      margin: 0;
    }
  }

  .icon-btn {
    position: absolute;
    bottom: 6px;
    right: 35px;
  }
`;

const summaryCSS = css`
  position: relative;
  padding-right: ${Spacing.large}px;
  font-size: ${FontSizes.small}px;
  text-align: center;
  padding: 0 15.5px;
  margin-bottom: ${Spacing.large}px;

  ${media.tablet} {
    padding: 0 ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding: 0 ${Spacing.xxlarge}px;
  }

  button {
    position: absolute;
    margin-left: ${Spacing.xxxsmall}px;
    bottom: ${Spacing.xxxsmall}px;
    right: 0;
  }
`;

const cntContainerCSS = (theme: Theme) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 22.5px;

  .bar::before {
    content: '';
    display: flex;
    width: 1px;
    height: 12px;
    border-radius: 0.5px;
    background-color: ${BAR_COLOR};
    margin: 0 ${Spacing.xsmall}px;
  }

  .cnt {
    display: flex;
    align-items: center;
    gap: ${Spacing.xxsmall}px;
    line-height: 14px;
    ${Typography.h6}
  }
  .text {
    line-height: 14.85px;
    color: ${theme.var.gray600};
    ${Typography.body11};
  }
`;

const buttonContainerCSS = () => css`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: ${Spacing.xxsmall}px;

  button {
    ${Typography.h5};
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    box-shadow: none;
    line-height: 16px;
  }
  .storyBtn {
    width: 122px;
  }
  .chatBtn {
    width: 182px;
  }
  .play-btn {
    width: 156px;
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
  }

  ${media.tablet} {
    gap: 14px;

    .storyBtn {
      width: 140px;
    }
    .chatBtn {
      width: 200px;
    }
    .play-btn {
      width: 180px;
    }
  }
  ${media.desktop} {
    gap: 16px;

    .storyBtn {
      width: 160px;
    }
    .chatBtn {
      width: 220px;
    }
    .play-btn {
      width: 200px;
    }
  }
`;

interface ICharacterDetailInfoAreaProps {
  character: IChatCharacter;
  isNewCharacter: boolean;
  handleClickTag: (id: number, name: string) => void;
  handleNavigateUser: (id: number) => void;
  handleChatButtonClick: () => Promise<boolean | void>;
}

const CharacterDetailInfoArea = ({
  character,
  isNewCharacter,
  handleClickTag,
  handleNavigateUser,
  handleChatButtonClick,
}: ICharacterDetailInfoAreaProps) => {
  const theme = useTheme();
  const { isCheming } = useGlobalContext();
  const [isSummaryOpen, setSummaryOpen] = useState(false);
  const [summary1, summary2] = trimString(character?.summary ?? '');
  const [isTagOpen, setIsTagOpen] = useState(false);

  const tagContainerRef = useRef<HTMLDivElement>(null);
  const [rowCount, setRowCount] = useState(1);

  const { useFetchUser } = useUserQuery();
  const { data: user } = useFetchUser(character?.player?.user_id);

  useEffect(() => {
    const calculateRowCount = () => {
      if (tagContainerRef.current) {
        const containerWidth = tagContainerRef.current.clientWidth;
        const items = tagContainerRef.current.children;
        const gapWidth = 8;

        let currentRowWidth = 0;
        let currentRowCount = 1;

        for (let i = 0; i < items.length; i++) {
          const itemWidth = items[i].clientWidth;
          if (
            currentRowWidth + itemWidth + gapWidth * (i - 1) >
            containerWidth
          ) {
            currentRowCount++;
            currentRowWidth = 0;
          }
          currentRowWidth += itemWidth + gapWidth * (i - 1);
        }

        setRowCount(currentRowCount);
      }
    };

    calculateRowCount();
  }, [isTagOpen, tagContainerRef]);

  return (
    <>
      <div css={coverImageCSS}>
        <EFinderImage
          image={character.cover_image as IEFinderObject<'image'>}
        />

        <Pad />
      </div>
      <div css={profileCSS}>
        <div className="character-avatar">
          <RoundAvatar picture={character.profile_image} size={80} />
          {isNewCharacter && (
            <Badge
              text={'새 캐릭터 등장 👀'}
              backgroundColor={
                isCheming
                  ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                  : 'linear-gradient(90deg, #04C8D0, #1590D5)'
              }
              fontSize={10}
              className={'new-badge'}
            />
          )}
        </div>
        <Badge
          text={character?.can_have_visual ? '비주얼 캐릭터' : '일반 캐릭터'}
          backgroundColor={
            isCheming
              ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
              : 'linear-gradient(90deg, #04C8D0, #1590D5)'
          }
          fontSize={10}
          border={true}
          color={theme.colors.main500}
        />
        <h5>{character.name}</h5>
        <p>{character.greeting}</p>
        {user && (
          <p
            className="characterCreater"
            onClick={() => handleNavigateUser(user.id)}
          >
            by {user.nickname}
          </p>
        )}
      </div>

      <div css={tagContainerCSS(isTagOpen)}>
        <div className="tagList" ref={tagContainerRef}>
          {character.tags.map((item, index) => (
            <Tag
              {...item}
              key={`tag_${index}`}
              onClick={() => handleClickTag(item.id, item.name)}
            />
          ))}
        </div>
        {rowCount > 1 && (
          <button
            className="icon-btn"
            onClick={() => setIsTagOpen((prev) => !prev)}
          >
            <Icon
              icon={isTagOpen ? ArrowUp : ArrowDown}
              size={Sizes.small}
              color={theme.var.gray500}
            />
          </button>
        )}
      </div>
      <Pad>
        {character.type === 'official' && (
          <div css={summaryCSS}>
            <p className="multiline">
              {summary1}
              {isSummaryOpen && summary2}
            </p>
            {summary2 && (
              <button
                className="icon-btn"
                onClick={() => setSummaryOpen((prev) => !prev)}
              >
                <Icon
                  icon={isSummaryOpen ? ArrowUp : ArrowDown}
                  size={Sizes.small}
                  color={theme.var.gray500}
                />
              </button>
            )}
          </div>
        )}

        <div css={cntContainerCSS}>
          <p className="cnt">
            {formatComma(character.stat.message_count)}
            <span className="text">플레이 수</span>
          </p>
          <span className="bar" />
          <p className="cnt">
            {formatComma(character.stat.like_count)}
            <span className="text">좋아요</span>
          </p>
        </div>
        <div css={buttonContainerCSS}>
          <Button className="play-btn" onClick={handleChatButtonClick}>
            케미 쌓아보기 😋
          </Button>
        </div>
      </Pad>
    </>
  );
};

export default CharacterDetailInfoArea;
